'use client';

import { useTranslations } from 'next-intl';
import Image from 'next/image';

const PrivacyPolicy = () => {
    const t = useTranslations('PrivacyPolicy');

    return (
        <div className="flex flex-col min-h-screen">
            {/* Hero Section with Background Image */}
            <section className="relative h-[300px] flex items-center justify-center">
                <div
                    className="absolute inset-0"
                >
                    <Image
                        src="/images/Privacy-policy-banner.png"
                        alt="Privacy Policy Background"
                        fill
                        className="object-cover bg-center bg-no-repeat"
                        priority
                    />
                    <div className="absolute inset-0 bg-black/50" />
                </div>
                <h1 className="text-5xl font-bold text-white relative z-10">
                    {t('header.hero')}
                </h1>
            </section>

            {/* Content Section */}
            <section className="bg-[#1a1b2b] min-h-screen px-4 py-24">
                <div className="max-w-7xl mx-auto space-y-8">
                    {/* Header */}
                    <div className="space-y-8">
                        <h2 className="text-xl font-bold text-white">{t('header.title')}</h2>
                        <p className="text-white text-base">{t('header.last_modified')}</p>
                    </div>

                    {/* Introduction */}
                    <div
                        className="text-white text-base [&_a]:text-[#f88523] [&_a]:no-underline [&_a:hover]:underline"
                        dangerouslySetInnerHTML={{ __html: t.raw('introduction') }}
                    />

                    {/* Policy Scope Section */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-semibold text-white">{t('sections.policy_scope.title')}</h2>
                        <div className="space-y-2 text-white text-base">
                            {t.raw('sections.policy_scope.applies_to').map((item: string, index: number) => (
                                <p key={index}>{item}</p>
                            ))}
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-white">{t('sections.policy_scope.does_not_apply.title')}</h3>
                            <div className="space-y-2 text-white text-base">
                                {t.raw('sections.policy_scope.does_not_apply.items').map((item: string, index: number) => (
                                    <p key={index}>{item}</p>
                                ))}
                            </div>
                        </div>

                        <p className="text-white text-base">{t('sections.policy_scope.note')}</p>
                    </div>

                    {/* Children Section */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-semibold text-white">{t('sections.children.title')}</h2>
                        <div
                            className="text-white text-base"
                            dangerouslySetInnerHTML={{ __html: t.raw('sections.children.content') }}
                        />
                    </div>

                    {/* Information Collection Section */}
                    <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-white">{t('sections.information_collection.title')}</h2>

                        {/* Provided Info */}
                        <div className="space-y-4">
                            <h3 className="text-white font-semibold text-lg">{t('sections.information_collection.provided_info.title')}</h3>
                            <div className="space-y-4 text-white text-base">
                                {t.raw('sections.information_collection.provided_info.items').map((item: string, index: number) => (
                                    <p key={index}>{item}</p>
                                ))}
                            </div>
                        </div>

                        {/* Automatic Info */}
                        <div className="space-y-4">
                            <h3 className="text-white font-semibold text-lg">{t('sections.information_collection.automatic_info.title')}</h3>
                            <div className="space-y-4 text-white text-base">
                                {t.raw('sections.information_collection.automatic_info.items').map((item: string, index: number) => (
                                    <p key={index}>{item}</p>
                                ))}
                            </div>
                        </div>

                        {/* Statistical Note */}
                        <p className="text-white text-base">{t('sections.information_collection.statistical_note')}</p>

                        {/* Usage Purposes */}
                        <div className="space-y-4 text-white text-base">
                            {t.raw('sections.information_collection.usage_purposes').map((item: string, index: number) => (
                                <p key={index}>{item}</p>
                            ))}
                        </div>

                        {/* Technologies */}
                        <div className="space-y-4">
                            <h3 className="text-white font-semibold text-lg">{t('sections.information_collection.technologies.title')}</h3>
                            <div className="space-y-6 text-white text-base">
                                <p>
                                    <span className="text-white font-semibold">Cookies (or browser cookies). </span>
                                    {t('sections.information_collection.technologies.types.browser_cookies')}
                                </p>
                                <p>
                                    <span className="text-white font-semibold">Session Cookies. </span>
                                    {t('sections.information_collection.technologies.types.session_cookies')}
                                </p>
                                <p>
                                    <span className="text-white font-semibold">Flash Cookies. </span>
                                    {t('sections.information_collection.technologies.types.flash_cookies')}
                                </p>
                                <p>
                                    <span className="text-white font-semibold">Web Beacons. </span>
                                    {t('sections.information_collection.technologies.types.web_beacons')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Information Usage Section */}
                    <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-white">
                            {t('sections.information_usage.title')}
                        </h2>

                        {/* Introduction */}
                        <p className="text-white text-base">
                            {t('sections.information_usage.intro')}
                        </p>

                        {/* Usage Purposes List */}
                        <div className="space-y-4 text-white text-base">
                            {t.raw('sections.information_usage.usage_purposes').map((item: string, index: number) => (
                                <p key={index}>{item}</p>
                            ))}
                        </div>

                        {/* Aggregated Data */}
                        <p className="text-white text-base">
                            {t('sections.information_usage.aggregated_data')}
                        </p>
                    </div>

                    {/* Information Disclosure Section */}
                    <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-white">
                            {t('sections.information_disclosure.title')}
                        </h2>

                        {/* Introduction */}
                        <p className="text-white text-base">
                            {t('sections.information_disclosure.intro')}
                        </p>

                        {/* General Disclosure List */}
                        <div className="space-y-4 text-white text-base">
                            {t.raw('sections.information_disclosure.general_disclosure').map((item: string, index: number) => (
                                <p key={index}>{item}</p>
                            ))}
                        </div>

                        {/* Additional Disclosure */}
                        <p className="text-white text-base">
                            {t('sections.information_disclosure.additional_intro')}
                        </p>

                        {/* Additional Disclosure List */}
                        <div className="space-y-4 text-white text-base">
                            {t.raw('sections.information_disclosure.additional_disclosure').map((item: string, index: number) => (
                                <p key={index}>{item}</p>
                            ))}
                        </div>

                        {/* Note */}
                        <p className="text-white text-base">
                            {t('sections.information_disclosure.note')}
                        </p>
                    </div>

                    {/* Blockchain Privacy Section */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-semibold text-white">{t('sections.blockchain_privacy.title')}</h2>
                        <div
                            className="text-white text-base"
                            dangerouslySetInnerHTML={{ __html: t.raw('sections.blockchain_privacy.content') }}
                        />
                    </div>

                    {/* Your Choices Section */}
                    <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-white">
                            {t('sections.your_choices.title')}
                        </h2>

                        {/* Introduction */}
                        <p className="text-white text-base">
                            {t('sections.your_choices.intro')}
                        </p>

                        {/* Tracking Technologies */}
                        <div className="text-white text-base">
                            <p>
                                <span className="text-white font-semibold text-base">{t('sections.your_choices.tracking.title')}</span>
                                {' '}
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: t.raw('sections.your_choices.tracking.content')
                                    }}
                                    className="[&_a]:text-[#f88523] [&_a]:no-underline [&_a:hover]:underline"
                                />
                            </p>
                        </div>
                    </div>

                    {/* Third Party Section */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-semibold text-white">{t('sections.third_party.title')}</h2>
                        <p className="text-white text-base">{t('sections.third_party.content')}</p>
                    </div>

                    {/* Data Security Section */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-semibold text-white">{t('sections.data_security.title')}</h2>
                        <p className="text-white text-base">{t('sections.data_security.measures')}</p>
                        <p className="text-white text-base">{t('sections.data_security.user_responsibility')}</p>
                        <p className="text-white text-base">{t('sections.data_security.disclaimer')}</p>
                    </div>

                    {/* EU Disclosures Section */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-semibold text-white">{t('sections.eu_disclosures.title')}</h2>

                        <p className="text-white text-base">{t('sections.eu_disclosures.intro')}</p>

                        <div className="space-y-4 text-white text-base">
                            {t.raw('sections.eu_disclosures.rights').map((item: string, index: number) => (
                                <p key={index}>{item}</p>
                            ))}
                        </div>

                        <p className="text-white text-base">{t('sections.eu_disclosures.complaint_intro')}</p>

                        <div className="space-y-2 text-white text-base">
                            {t.raw('sections.eu_disclosures.authority_links').map((link: string, index: number) => (
                                <div
                                    key={index}
                                    className="[&_a]:text-[#f88523] [&_a]:no-underline [&_a:hover]:underline"
                                    dangerouslySetInnerHTML={{ __html: link }}
                                />
                            ))}
                        </div>

                        <p className="text-white text-base">{t('sections.eu_disclosures.contact')}</p>
                    </div>

                    {/* Policy Changes Section */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-semibold text-white">{t('sections.policy_changes.title')}</h2>
                        <p className="text-white text-base">{t('sections.policy_changes.content')}</p>
                    </div>

                    {/* Questions Section */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-semibold text-white">
                            {t('sections.questions.title')}
                        </h2>

                        <div
                            className="text-white [&_a]:text-[#f88523] [&_a]:no-underline [&_a:hover]:underline"
                            dangerouslySetInnerHTML={{
                                __html: t.raw('sections.questions.content')
                            }}
                        />
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PrivacyPolicy;
